import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'
import {
  fakeoutPick, fetchFakeoutState,
  type FakeoutResult, type FakeoutState,
} from '../../lib/api/wordGames'
import { WordWeeklyChart } from './WordWeeklyChart'
import { HowToOverlay } from './HowToOverlay'

const TITLE = 'FAKE OUT'

/**
 * The server's answer when the game day flipped under an open screen
 * (midnight Mountain time): yesterday's round no longer exists, or the
 * position the screen is holding belongs to it. Both mean "load today".
 */
const DAY_ROLLED = /No round in progress|Out of step/i

const HOW_TO = [
  'Six words a day. Each shows four definitions, and one of them is real.',
  'A right answer is worth 100 points, and every second you take costs 5 of them. A right answer is never worth less than 20.',
  'Spot all six for a 200 point bonus.',
  'Daily scores add up Monday through Sunday. Top of the chart wins the week and a stamp.',
]

interface Props {
  onBack: () => void
}

/**
 * The daily bluff quiz, dealt one word at a time. The server serves a
 * word, silently clocks the answer (5 pts per second off a correct
 * pick), reveals the truth, and only deals the next word when asked,
 * so reading the reveal costs nothing. Refreshing mid-quiz resumes
 * exactly where the server left off.
 */
export function FakeOutScreen({ onBack }: Props) {
  const t = useT()
  const [state, setState] = useState<FakeoutState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** The just-answered word's reveal, shown between words. */
  const [lastResult, setLastResult] = useState<FakeoutResult | null>(null)
  const [runningScore, setRunningScore] = useState(0)
  const [showHowTo, setShowHowTo] = useState(false)
  const [chartRefresh, setChartRefresh] = useState(0)

  /** Game day of the state on screen; a change clears yesterday's leftovers. */
  const dayRef = useRef<string | null>(null)

  async function loadState() {
    try {
      const s = await fetchFakeoutState()
      if (dayRef.current !== null && dayRef.current !== s.day) {
        setLastResult(null)
        setSelected(null)
        setError(null)
      }
      dayRef.current = s.day
      setState(s)
      setRunningScore(s.score ?? 0)
    } catch (e) {
      setError(readError(e))
    }
  }

  useEffect(() => { void loadState() }, [])

  // Coming back to the foreground (the app slept overnight on this
  // screen) re-reads the server so a new day replaces yesterday's word.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void loadState()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function lockIn() {
    if (!state?.position || !selected || busy) return
    setBusy(true)
    try {
      const r = await fakeoutPick(state.position, selected)
      setSelected(null)
      setLastResult(r.result)
      setRunningScore(r.score)
      if (r.done) {
        setLastResult(null)
        setChartRefresh(n => n + 1)
        await loadState()
      }
    } catch (e) {
      const msg = readError(e)
      if (DAY_ROLLED.test(msg)) {
        // Midnight passed while this word sat on screen. Yesterday is
        // over (it scores whatever was banked); deal today instead of
        // leaving the error over a dead word.
        setSelected(null)
        setLastResult(null)
        setError(null)
        await loadState()
      } else {
        setError(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  async function nextWord() {
    setLastResult(null)
    await loadState()
  }

  const playing = state && !state.entered && state.available !== false

  return (
    <div className="screen">
      <BackgroundTitle text={TITLE} color="#8FAF52" />
      <PageHeader
        title={TITLE}
        subtitle={t('One of these definitions is telling the truth')}
        onBack={onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-8 relative z-10">
        <button
          type="button"
          onClick={() => setShowHowTo(true)}
          className="w-full mb-3 py-2.5 rounded-full border-2 border-brand-border bg-white
                     text-xs font-semibold uppercase tracking-widest text-brand-muted
                     active:scale-[0.98] transition-transform"
        >
          {t('How to play')}
        </button>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-3">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {!state && !error && (
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
        )}

        {state && !state.entered && state.available === false && (
          <p className="text-sm text-brand-muted text-center py-10">
            {t('No words today. Come back tomorrow.')}
          </p>
        )}

        {/* Between-words reveal */}
        {playing && lastResult && (
          <>
            <div className={`card p-4 mt-1 rounded-2xl border-2 bg-white
                             ${lastResult.correct ? 'border-brand-success' : 'border-brand-danger'}`}>
              <div className="flex items-center justify-between">
                <p className="font-display font-bold uppercase tracking-wide text-brand-text">
                  {lastResult.word}
                </p>
                <span className={`font-display font-bold ${lastResult.correct ? 'text-brand-success' : 'text-brand-danger'}`}>
                  {lastResult.correct ? `+${lastResult.pts}` : t('FOOLED')}
                </span>
              </div>
              <p className="text-sm text-brand-text mt-1.5">{lastResult.real_def}</p>
              {!lastResult.correct && (
                <p className="text-xs text-brand-muted mt-1.5 line-through">{lastResult.pick}</p>
              )}
            </div>
            <button
              onClick={() => void nextWord()}
              className="w-full mt-4 py-3.5 rounded-2xl font-display font-bold text-lg
                         bg-brand-text text-white active:scale-[0.98] transition-transform"
            >
              {t('Next word')}
            </button>
            <p className="text-center text-xs text-brand-muted mt-3">
              {t('{n} pts so far', { n: runningScore })}
            </p>
          </>
        )}

        {/* The word in play */}
        {playing && !lastResult && state.word && (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted mt-1">
              {t('Word {a} of {b}', { a: state.position ?? 0, b: state.total ?? 0 })}
            </p>
            <h2 className="font-display font-bold text-4xl text-brand-text uppercase tracking-wide mt-1 mb-4">
              {state.word}
            </h2>
            <div className="space-y-2.5">
              {state.defs?.map(def => (
                <button
                  key={def}
                  onClick={() => setSelected(def)}
                  className={`w-full rounded-2xl p-4 text-left text-sm leading-relaxed
                              border-2 transition-colors
                              ${selected === def
                                ? 'border-brand-text bg-brand-elevated font-semibold'
                                : 'border-brand-border bg-white'}`}
                >
                  {def}
                </button>
              ))}
            </div>
            <button
              onClick={() => void lockIn()}
              disabled={!selected || busy}
              className="w-full mt-5 py-3.5 rounded-2xl font-display font-bold text-lg
                         bg-brand-text text-white active:scale-[0.98] transition-transform
                         disabled:opacity-40"
            >
              {busy ? t('Checking…') : t('Lock it in')}
            </button>
          </>
        )}

        {/* Day complete: full reveal */}
        {state?.entered && (
          <>
            <div className="card p-4 mt-1 bg-game-fakeout-light border-2 border-game-fakeout-dark rounded-2xl text-center">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                {t('Today')}
              </p>
              <p className="font-display font-bold text-4xl text-brand-text mt-1">
                {t('{n} pts', { n: state.score ?? 0 })}
              </p>
              <p className="text-sm text-brand-muted mt-1">
                {t('{a} of {b} spotted', { a: state.correct ?? 0, b: state.total ?? 0 })}
                {state.correct === state.total ? t(' · Perfect bonus +200') : ''}
              </p>
            </div>

            <div className="space-y-2.5 mt-4">
              {state.detail?.map(row => (
                <div key={row.position}
                     className="bg-white border border-brand-border rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-display font-bold uppercase tracking-wide text-brand-text">
                      {row.word}
                    </p>
                    <span className={`text-xs font-bold ${row.correct ? 'text-brand-success' : 'text-brand-danger'}`}>
                      {/* Entries recorded before per-word timing have no
                          pts field; show the plain verdict for those. */}
                      {row.correct
                        ? (row.pts != null ? t('SPOTTED +{n}', { n: row.pts }) : t('SPOTTED'))
                        : t('FOOLED')}
                    </span>
                  </div>
                  <p className="text-sm text-brand-text mt-1.5">{row.real_def}</p>
                  {!row.correct && (
                    <p className="text-xs text-brand-muted mt-1.5 line-through">{row.pick}</p>
                  )}
                </div>
              ))}
            </div>

            <WordWeeklyChart game="fake_out" refresh={chartRefresh}
                             accentClass="text-game-fakeout-dark" />
          </>
        )}
      </div>

      {showHowTo && (
        <HowToOverlay title={TITLE} lines={HOW_TO.map(l => t(l))} accent="#6B8A34" image="/games/fake-out.webp"
                      onClose={() => setShowHowTo(false)} />
      )}
    </div>
  )
}
